import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AppShell } from "../components/AppShell";
import {
  addDomain,
  deleteDomain,
  fetchDomains,
  normalizeHostname,
  updateDomain,
} from "../api/domains";
import { useLanguage } from "../i18n/LanguageContext";
import { t } from "../i18n";

export const WebsitesPage = () => {
  const { portal } = useLanguage();
  const copy = portal.websites;
  const common = portal.common;
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [text, setText] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editedHostname, setEditedHostname] = useState("");
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const next = await fetchDomains();
      setDomains(Array.isArray(next) ? next : []);
    } catch (err) {
      setError(err?.message || copy.loadFailed);
      setDomains([]);
    } finally {
      setLoading(false);
    }
  }, [copy.loadFailed]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const lines = useMemo(
    () =>
      text
        .split("\n")
        .map((line) => normalizeHostname(line))
        .filter(Boolean),
    [text]
  );

  const handleAdd = async (event) => {
    event.preventDefault();
    if (lines.length === 0) return;

    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const existing = new Set(domains.map((domain) => domain.hostname));
      const toAdd = lines.filter((hostname) => !existing.has(hostname));
      if (toAdd.length === 0) {
        setError(copy.alreadyAdded);
        return;
      }

      for (const hostname of toAdd) {
        await addDomain(hostname);
      }
      setText("");
      setSuccess(t(copy.added, { count: toAdd.length }));
      await loadData();
    } catch (err) {
      setError(err?.message || copy.addFailed);
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async (domainId) => {
    const hostname = normalizeHostname(editedHostname);
    if (!hostname) {
      setError(copy.invalidHostname);
      return;
    }

    setSaving(true);
    setError("");
    setSuccess("");
    try {
      await updateDomain(domainId, hostname);
      setEditingId(null);
      setEditedHostname("");
      setSuccess(copy.updated);
      await loadData();
    } catch (err) {
      setError(err?.message || copy.updateFailed);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (domain) => {
    const confirmed = window.confirm(
      t(copy.confirmDelete, { hostname: domain.hostname })
    );
    if (!confirmed) return;

    setError("");
    setSuccess("");
    try {
      await deleteDomain(domain._id);
      if (editingId === domain._id) {
        setEditingId(null);
        setEditedHostname("");
      }
      setSuccess(copy.removed);
      await loadData();
    } catch (err) {
      setError(err?.message || copy.deleteFailed);
    }
  };

  return (
    <AppShell title={copy.title} description={copy.description}>
      {error && <div className="app-alert">{error}</div>}
      {success && <div className="app-alert success">{success}</div>}

      <section className="app-form-card">
        <h2>{copy.addTitle}</h2>
        <p className="app-hint">{copy.hint}</p>
        <form onSubmit={handleAdd} className="app-form">
          <label htmlFor="domain-text">{copy.urlsLabel}</label>
          <textarea
            id="domain-text"
            rows={4}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={"https://chart.example.com\nehr.clinic.org"}
            disabled={saving}
          />
          {lines.length > 0 && (
            <p className="app-hint">
              {t(copy.willAdd, { list: lines.join(", ") })}
            </p>
          )}
          <div className="app-form-actions">
            <button
              type="submit"
              className="app-primary"
              disabled={saving || lines.length === 0}
            >
              {saving
                ? common.saving
                : lines.length > 1
                  ? t(copy.addMany, { count: lines.length })
                  : copy.addOne}
            </button>
          </div>
        </form>
      </section>

      <section className="app-table-card">
        <div className="app-toolbar">
          <span className="app-count">
            {loading
              ? common.loading
              : t(copy.countAdded, { count: domains.length })}
          </span>
        </div>
        <div className="app-table-wrap">
          <table className="app-table">
            <thead>
              <tr>
                <th>{copy.colWebsites}</th>
                <th>{copy.colActions}</th>
              </tr>
            </thead>
            <tbody>
              {!loading && domains.length === 0 && (
                <tr>
                  <td colSpan={2} className="app-empty">
                    {copy.empty}
                  </td>
                </tr>
              )}
              {domains.map((domain) => (
                <tr key={domain._id}>
                  <td className="app-text-cell">
                    {editingId === domain._id ? (
                      <input
                        type="text"
                        value={editedHostname}
                        onChange={(e) => setEditedHostname(e.target.value)}
                        disabled={saving}
                      />
                    ) : (
                      domain.hostname
                    )}
                  </td>
                  <td className="app-actions-cell">
                    {editingId === domain._id ? (
                      <>
                        <button
                          type="button"
                          className="app-link-btn"
                          onClick={() => handleSave(domain._id)}
                          disabled={saving}
                        >
                          {common.save}
                        </button>
                        <button
                          type="button"
                          className="app-link-btn"
                          onClick={() => {
                            setEditingId(null);
                            setEditedHostname("");
                          }}
                          disabled={saving}
                        >
                          {common.cancel}
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="app-link-btn"
                          onClick={() => {
                            setEditingId(domain._id);
                            setEditedHostname(domain.hostname);
                          }}
                        >
                          {common.edit}
                        </button>
                        <button
                          type="button"
                          className="app-link-btn danger"
                          onClick={() => handleDelete(domain)}
                        >
                          {common.delete}
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </AppShell>
  );
};
